import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isProblemError } from '../../api/client';
import { useGetMe } from '../../api/generated/identity/identity';
import {
  useCreateBillingPortalSession,
  useCreateCheckoutSession,
  useGetMyBilling,
  useListMyPaymentMethods,
} from '../../api/generated/ledger/ledger';
import type { Charge, PaymentMethod } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { formatDateInPersonTimezone } from '../../i18n/datetime';
import { useMoneyAttempt } from './idempotency';
import { formatMoney, isSettled, openTotals } from './money';
import { useAllMyCharges } from './myCharges';
import styles from './billing.module.css';

/** Estados em que ainda faz sentido oferecer pagamento. */
const PAYABLE = new Set(['pending', 'overdue']);

/**
 * Portal financeiro do aluno.
 *
 * Tres regras estruturam esta tela, e as tres sao de seguranca, nao de layout:
 *
 * 1. **Nao existe campo de cartao aqui.** Checkout e portal do provedor sao
 *    hospedados; a tela so navega para a URL que o backend devolveu. Nenhuma
 *    chave do provedor entra no bundle, e a forma de garantir isso e nao ter
 *    para onde ela ir.
 * 2. **Voltar do checkout nao significa pago.** O estado do pagamento vem do
 *    ledger, que so muda quando o webhook chega. Escrever "pago" porque o
 *    navegador voltou e dizer ao aluno algo que o backend ainda nao sabe.
 * 3. **Valor e centavo inteiro** ate o ultimo passo. Ver `money.ts`.
 */
export function StudentBilling() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;

  const returning = useReturningFromCheckout();
  const billingQuery = useGetMyBilling({
    query: {
      enabled: !!me,
      // Na volta do checkout o ledger ainda espera o webhook. Reconsultar com
      // recuo e o que faz a tela sair de "em processamento" sozinha quando o
      // pagamento confirma — sem prometer nada antes disso.
      refetchInterval: returning ? 4000 : false,
    },
  });
  const methodsQuery = useListMyPaymentMethods({ query: { enabled: !!me } });
  /**
   * O EXTRATO INTEIRO, e ele e a UNICA fonte de fatura desta tela.
   *
   * `getMyBilling` continua respondendo pela situacao corrente — bloqueio de
   * acesso, vencimento seguinte, moeda —, mas o `charges` que ele carrega e uma
   * PAGINA de 50, e era dessa pagina que saiam tanto o resumo em dinheiro
   * quanto a lista do que esta em cobranca. Ver `useAllMyCharges` para o porque
   * de somar sobre uma pagina subestimar a divida em silencio.
   *
   * A leitura paginada ja existia aqui, para o historico, e tambem parava na
   * primeira pagina: o aluno com mais de 50 faturas perdia as antigas — na
   * mesma tela em que ele vai buscar comprovante de pagamento.
   */
  const {
    charges: allCharges,
    complete: chargesComplete,
    failed: chargesFailed,
  } = useAllMyCharges({ enabled: !!me, refetchInterval: returning ? 4000 : false });
  const portal = useCreateBillingPortalSession();

  if (!me) return null;

  const billing = billingQuery.data;
  const methods = methodsQuery.data;
  // A moeda vem de `outstanding`, que continua sendo o campo do contrato que a
  // declara. O que a tela deixou de usar dele e o **valor**, porque ele soma
  // `processing` junto — ver `openTotals`.
  const totals = chargesComplete
    ? openTotals(allCharges, billing?.outstanding.currency ?? 'BRL')
    : undefined;
  // Faturas liquidadas nao pedem nada: elas moram no historico. Sem este corte,
  // "Faturas" e "Historico de faturas" mostravam a mesma coisa duas vezes, e a
  // segunda lista nao acrescentava nem informacao nem acao.
  const openCharges = allCharges.filter((charge) => !isSettled(charge.state));

  return (
    <div className={styles.page}>
      <h1>{t('billing.title')}</h1>

      {returning ? <Alert tone="info">{t('billing.processing')}</Alert> : null}
      {billingQuery.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
      {billing?.blockedAccess ? (
        <Alert tone="error" title={t('billing.accessBlocked.title')}>
          {t('billing.accessBlocked.detail')}
        </Alert>
      ) : null}

      {billing ? (
        <>
          <section className={styles.summary} aria-label={t('billing.summary')}>
            {/*
              Numero de divida so aparece quando ele e a divida INTEIRA. Com
              pagina por buscar, a soma que a tela consegue fazer e menor que a
              que o banco faz, e um total menor lido como total e a unica forma
              de erro desta tela que o aluno nao tem como perceber sozinho —
              ele nao sabe o que ficou atras do cursor. Enquanto a colecao nao
              fecha, a tela diz que esta carregando; se a leitura falhou, diz
              que falhou.
            */}
            {totals === undefined ? (
              chargesFailed ? (
                <Alert tone="error">{t('error.loadFailed')}</Alert>
              ) : (
                <p className={styles.figure}>{t('app.loading')}</p>
              )
            ) : (
              <>
                <p className={styles.figure}>
                  <span>{t('billing.outstanding')}</span>
                  <span className={styles.figureValue}>
                    {formatMoney(totals.awaitingPayment, me.locale)}
                  </span>
                </p>
                {/*
                  O que ja esta em processamento aparece **separado**, e so
                  quando existe: e dinheiro que o aluno ja mandou e sobre o qual
                  ele nao tem mais nada a fazer. Somado ao de cima, ele viraria
                  cobranca de novo — que e exatamente o defeito que esta
                  separacao desfaz. Zerado e ruido: nao ha nada a esperar do
                  banco.
                */}
                {totals.inProcessing.amountCents > 0 ? (
                  <p className={styles.figure}>
                    <span>{t('billing.chargeState.processing')}</span>
                    <span className={styles.figureValue}>
                      {formatMoney(totals.inProcessing, me.locale)}
                    </span>
                  </p>
                ) : null}
              </>
            )}
            <p className={styles.figure}>
              <span>{t('billing.nextDue')}</span>
              <span className={styles.figureValue}>
                {billing.nextDueOn
                  ? formatDateInPersonTimezone(billing.nextDueOn, me.timezone, me.locale)
                  : t('billing.nextDueNone')}
              </span>
            </p>
          </section>

          <PaymentMethods
            available={methods?.available ?? []}
            pixBlockedReason={methods?.pixBlockedReason ?? null}
            failed={methodsQuery.isError}
          />

          <div className={styles.actions}>
            <Button
              variant="secondary"
              loading={portal.isPending}
              onClick={() =>
                portal.mutate(undefined, {
                  onSuccess: (session) => {
                    window.location.assign(session.url);
                  },
                })
              }
            >
              {t('billing.portal')}
            </Button>
          </div>

          <section aria-label={t('billing.charges')}>
            <h2>{t('billing.charges')}</h2>
            {/*
              A frase e o que separa esta secao do historico logo abaixo. Sem
              ela as duas se chamavam "faturas" e traziam as mesmas linhas, e o
              aluno ficava procurando a diferenca que nao existia.
            */}
            <p className={styles.meta}>{t('billing.chargesExplained')}</p>
            {/*
              As duas secoes desta tela saem da MESMA colecao, e por isso as
              duas so afirmam vazio quando o cursor acabou. "Nenhuma fatura por
              aqui" dito a quem tem uma fatura vencida atras da pagina 1 e a
              frase que faz o aluno parar de procurar — e ela mentia por
              omissao, sem nenhum sinal de que a lista tinha acabado antes das
              faturas.
            */}
            {chargesFailed ? (
              <Alert tone="error">{t('error.loadFailed')}</Alert>
            ) : !chargesComplete ? (
              <p>{t('app.loading')}</p>
            ) : openCharges.length === 0 ? (
              <p>{t('billing.chargesEmpty')}</p>
            ) : null}
            <ul className={styles.list}>
              {openCharges.map((charge) => (
                <ChargeCard
                  key={charge.id}
                  charge={charge}
                  locale={me.locale}
                  timezone={me.timezone}
                  methods={methods?.available ?? []}
                />
              ))}
            </ul>
          </section>

          <section aria-label={t('billing.history')}>
            <h2>{t('billing.history')}</h2>
            <p className={styles.meta}>{t('billing.historyExplained')}</p>
            {/*
              Leitura que falhou nao e historico vazio. Sem este ramo, o 500 de
              `listMyCharges` caia na mesma lista vazia do aluno que nunca teve
              fatura, e a tela afirmava "Nenhuma fatura" a quem tem todas —
              apagando da tela a prova de pagamento que ele veio procurar. Dizer
              "nao consegui carregar" e pior de ler e melhor de acreditar: uma
              frase pede recarregar, a outra faz duvidar do que ja foi pago.

              `isFetchNextPageError` entra junto por `chargesFailed`: `isError`
              fala so da primeira pagina, e a que falha NO MEIO do extrato
              deixaria o historico truncado com a mesma cara de historico
              completo. E o ramo de "carregando" existe pelo mesmo motivo do
              vazio: enquanto ha cursor por seguir, "Nenhuma fatura" e uma
              afirmacao que o servidor ainda nao fez.
            */}
            {chargesFailed ? (
              <Alert tone="error">{t('error.loadFailed')}</Alert>
            ) : !chargesComplete ? (
              <p>{t('app.loading')}</p>
            ) : allCharges.length === 0 ? (
              <p>{t('billing.historyEmpty')}</p>
            ) : (
              <ul className={styles.list}>
                {allCharges.map((charge) => (
                  // O rotulo diz que este item e o do historico. A fatura ainda
                  // em cobranca aparece acima tambem, e dois itens com o mesmo
                  // nome acessivel deixariam qualquer asercao ambigua —
                  // inclusive para quem navega por leitor de tela.
                  <li
                    key={charge.id}
                    className={styles.card}
                    aria-label={t('billing.historyItem', { description: charge.description })}
                  >
                    <p>
                      <strong>{formatMoney(charge.amount, me.locale)}</strong> ·{' '}
                      <span>{t(`billing.chargeState.${charge.state}`)}</span>
                    </p>
                    <p className={styles.meta}>
                      {t('billing.dueOn', {
                        date: formatDateInPersonTimezone(charge.dueOn, me.timezone, me.locale),
                      })}
                    </p>
                    {/*
                      O comprovante mora aqui porque e aqui que a fatura paga
                      vive. Enquanto a lista de cima repetia o historico, o link
                      aparecia nas duas; agora ela so mostra o que ainda esta em
                      cobranca, e sem esta linha o comprovante nao teria onde
                      aparecer.
                    */}
                    {charge.receiptUrl ? (
                      <p>
                        <a href={charge.receiptUrl} target="_blank" rel="noopener noreferrer">
                          {t('billing.receipt')}
                        </a>
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-label={t('billing.subscriptions')}>
            <h2>{t('billing.subscriptions')}</h2>
            {billing.subscriptions.length === 0 ? (
              <p>{t('billing.subscriptionsEmpty')}</p>
            ) : null}
            <ul className={styles.list}>
              {billing.subscriptions.map((subscription) => (
                <li key={subscription.id} className={styles.card}>
                  <p>{t(`billing.subscriptionState.${subscription.state}`)}</p>
                  {subscription.currentPeriodEnd ? (
                    <p className={styles.meta}>
                      {t('billing.periodEnd', {
                        date: formatDateInPersonTimezone(
                          subscription.currentPeriodEnd,
                          me.timezone,
                          me.locale,
                        ),
                      })}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        </>
      ) : null}
    </div>
  );
}

function ChargeCard({
  charge,
  locale,
  timezone,
  methods,
}: {
  readonly charge: Charge;
  readonly locale: string;
  readonly timezone: string;
  readonly methods: readonly PaymentMethod[];
}) {
  const { t } = useTranslation();
  // O erro mora na fatura, e nao no botao, porque cada meio de pagamento tem a
  // sua propria mutacao — ver `CheckoutButton`. O aviso e um so por fatura:
  // repeti-lo ao lado de cada botao diria tres vezes a mesma coisa.
  const [checkoutError, setCheckoutError] = useState<unknown>(null);

  // O 503 do gate de preco provisorio nao e "erro inesperado": e um estado do
  // sistema, e a tela precisa dizer qual. Erro generico aqui faria o aluno
  // tentar de novo indefinidamente.
  const blockedByPlaceholder =
    isProblemError(checkoutError) && checkoutError.status === 503;

  return (
    // O `aria-label` nomeia a fatura pela descricao. Serve a quem navega por
    // leitor de tela e, de quebra, e o que permite ao teste falar de *uma*
    // fatura: sem isso, uma asercao sobre "Pago" casa com qualquer texto igual
    // em qualquer lugar da pagina, e passa sem provar nada.
    <li className={styles.card} aria-label={charge.description}>
      <p>
        <strong>{formatMoney(charge.amount, locale)}</strong> ·{' '}
        <span>{t(`billing.chargeState.${charge.state}`)}</span>
      </p>
      <p>{charge.description}</p>
      <p className={styles.meta}>
        {t('billing.dueOn', {
          date: formatDateInPersonTimezone(charge.dueOn, timezone, locale),
        })}
      </p>

      {charge.receiptUrl ? (
        <p>
          <a href={charge.receiptUrl} target="_blank" rel="noopener noreferrer">
            {t('billing.receipt')}
          </a>
        </p>
      ) : null}

      {blockedByPlaceholder ? (
        <Alert tone="warning" title={t('billing.blocked.title')}>
          {t('billing.blocked.detail')}
        </Alert>
      ) : null}

      {PAYABLE.has(charge.state) ? (
        <div className={styles.actions}>
          {methods.map((method) => (
            <CheckoutButton
              key={method}
              chargeId={charge.id}
              method={method}
              onResult={setCheckoutError}
            />
          ))}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Um botao, um meio de pagamento, **uma** chave de idempotencia.
 *
 * Os botoes nao compartilham a mutacao de proposito, e a razao e a chave: quem
 * tentasse cartao, fosse recusado e clicasse em Pix mandaria a mesma chave com
 * outro corpo — e a resposta correta do servidor a isso e devolver a sessao de
 * cartao que ele ja tinha criado. Com uma mutacao por meio, a chave repetida
 * so pode acontecer no que ela existe para cobrir: repetir o mesmo pagamento.
 *
 * De quebra, cada botao passa a ter o seu proprio "carregando", em vez de todos
 * acenderem juntos porque um deles foi clicado.
 */
function CheckoutButton({
  chargeId,
  method,
  onResult,
}: {
  readonly chargeId: string;
  readonly method: PaymentMethod;
  readonly onResult: (error: unknown) => void;
}) {
  const { t } = useTranslation();
  const attempt = useMoneyAttempt();
  const checkout = useCreateCheckoutSession({ request: attempt.request });

  return (
    <Button
      loading={checkout.isPending}
      onClick={() => {
        // O aviso da tentativa anterior sai antes desta comecar: mante-lo na
        // tela enquanto uma nova roda diria que o pagamento esta bloqueado
        // quando ninguem ainda sabe.
        onResult(null);
        checkout.mutate(
          { data: { chargeId, method } },
          {
            onSuccess: (session) => {
              // A sessao existe: esta tentativa acabou. Voltar do provedor sem
              // pagar comeca outra, e a outra precisa de chave propria.
              attempt.settled();
              window.location.assign(session.url);
            },
            onError: onResult,
          },
        );
      }}
    >
      {t(`billing.payWith.${method}`)}
    </Button>
  );
}

/**
 * Formas de pagamento disponiveis.
 *
 * Quando o Pix nao aparece, a tela **diz por que**. Esconder a opcao sem
 * explicacao deixa o aluno sem descobrir que basta cadastrar o CPF — e a §5.2
 * e explicita em que a falta do documento nao bloqueia a matricula.
 *
 * `failed` existe pela mesma razao, um nivel acima: a lista de meios que falhou
 * chegava aqui como lista vazia, indistinguivel de "o provedor nao oferece
 * nenhum meio". E lista vazia nao apaga so esta secao — e dela que saem os
 * botoes de pagar de cada fatura, entao o aluno ficava sem nenhuma forma de
 * pagar e sem uma linha na tela dizendo o motivo.
 */
function PaymentMethods({
  available,
  pixBlockedReason,
  failed,
}: {
  readonly available: readonly PaymentMethod[];
  readonly pixBlockedReason: string | null;
  readonly failed: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section aria-label={t('billing.methods')}>
      <h2>{t('billing.methods')}</h2>
      {failed ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
      <ul>
        {available.map((method) => (
          <li key={method}>{t(`billing.method.${method}`)}</li>
        ))}
      </ul>
      {!available.includes('pix') && pixBlockedReason ? (
        <Alert tone="info" title={t('billing.pixUnavailable')}>
          {t(`billing.pixBlocked.${pixBlockedReason}`)}
        </Alert>
      ) : null}
    </section>
  );
}

/**
 * Diz se a navegacao veio de uma volta do checkout hospedado.
 *
 * O provedor devolve o navegador para a URL de sucesso que o backend
 * registrou. A tela nao le nenhum dado de pagamento dessa URL — so o fato de
 * ter voltado, que basta para mostrar "em processamento" e reconsultar.
 */
function useReturningFromCheckout(): boolean {
  const [returning, setReturning] = useState(
    () => new URLSearchParams(window.location.search).has('checkout'),
  );

  useEffect(() => {
    if (!returning) return;
    // Para de reconsultar depois de dois minutos: passado esse tempo insistir e
    // ruido, e o aviso in-app do ledger e quem conta o desfecho.
    const timer = window.setTimeout(() => setReturning(false), 120_000);
    return () => window.clearTimeout(timer);
  }, [returning]);

  return returning;
}
